import React from "react";
import PropTypes from "prop-types";
import { throttle } from "throttle-debounce";
import { autocomplete } from "GlobalUtils/Autocomplete";
import { makeCancelable } from "GlobalUtils/CancelablePromise";
import { AREA_AUTOCOMPLETE_URL } from "GlobalConstant/index";
import AutoComplete from "@oxygen/core/lib/Autocomplete/Autocomplete";
import { platform as PLATFORM } from "GlobalEnum/Platform";
import { DEFAULT_AREA_NAME } from "GlobalConstant/Location";

const propTypes = {
  placeholder: PropTypes.string,
  // Object with city and area details
  cityLocation: PropTypes.object,
  // Callback function gets fired on input change
  onChange: PropTypes.func,
  // Set clear button for input field
  setClearButton: PropTypes.bool,
  // Callback function gets fired on input clear
  onClear: PropTypes.func,
  // Callback function gets fired on clear button click
  clearSelection: PropTypes.func,
  // Callback function gets fired on area selection
  setArea: PropTypes.func,
  // Set focus to input field
  setFocus: PropTypes.bool,
  // Remove City
  removeCity: PropTypes.func,
  //  platform
  platform: PropTypes.object,
};

const defaultProps = {
  placeholder: "Type your area",
  setClearButton: true,
  platform: PLATFORM.MOBILE,
};

/**
 * This Class is a container component for the {@link Autocomplete} component
 * to be used in global location screen.
 * @class AreaAutocomplete
 * @extends {React.Component}
 */

//The component is exported as unconnected component for custom handling
export class AreaAutocomplete extends React.Component {
  constructor(props) {
    super(props);
    this.apiRequest = null;
    this.state = {
      input: this.getAreaName(props.cityLocation.areaName),
      areaList: [],
      isFetching: false,
      isMenuShown: true,
    };
    this.fetchAreas = throttle(500, this.fetchAreas);
  }

  UNSAFE_componentWillReceiveProps = nextProps => {
    if (this.props.cityLocation.areaId !== nextProps.cityLocation.areaId) {
      this.setState({
        input: this.getAreaName(nextProps.cityLocation.areaName),
      });
    }
  };

  getAreaName = areaName => {
    return areaName && areaName.trim() !== DEFAULT_AREA_NAME ? areaName : "";
  };

  componentWillUnmount() {
    this.clearBlurTimmer();
    this.clearFocusTimmer();
    this.isUnsafe = true;
    this.apiRequest && this.apiRequest.cancel();
  }
  handleKeyDown = (event, isTagRemoval) => {
    if (isTagRemoval) {
      const { removeCity } = this.props;
      removeCity && removeCity();
    }
  };
  handleChange = event => {
    const { onChange } = this.props;

    const { value } = event.target;
    this.setState({
      input: value,
      isFetching: true,
    });
    this.fetchAreas(value);

    if (onChange) {
      onChange(event);
    }
  };

  /**
   * Method to make request for areas
   * and wrap the promise as cancelable
   * and set it in state.
   * @memberof AreaAutocomplete
   * @param {string} input User input value
   */
  fetchAreas = input => {
    if (input) {
      const { platform } = this.props;
      this.apiRequest && this.apiRequest.cancel();
      let apiRequest = makeCancelable(
        autocomplete(
          {
            url: AREA_AUTOCOMPLETE_URL,
            source: platform.id,
            resultCount: 5,
            cityId: this.props.cityLocation.cityId,
          },
          input
        )
      );
      apiRequest.promise
        .then(json => this.afterFetch(json))
        .catch(({ isCanceled, ...error }) =>
          console.log("isCanceled", isCanceled)
        );
      this.apiRequest = apiRequest;
    } else {
      this.setState({
        isFetching: false,
        areaList: [],
      });
    }
  };

  afterFetch = list => {
    if (this.state.input && list) {
      this.setState({
        areaList: list.map(item => {
          return {
            result: item.result,
            payload: JSON.stringify(item.payload),
          };
        }),
        isFetching: false,
      });
    }
  };

  handleClear = () => {
    const { clearSelection, onClear } = this.props;

    this.setState({
      input: "",
      areaList: [],
    });

    if (clearSelection) {
      clearSelection();
    }

    if (onClear) {
      onClear();
    }
  };
  handleBlur = () => {
    this.blurTimer = setTimeout(() => {
      if (!this.isUnsafe) {
        // This is done to prevent update after mount
        this.setState({
          isMenuShown: false,
        });
      }
    }, 250);
  };
  clearBlurTimmer = () => {
    this.blurTimer && clearTimeout(this.blurTimer);
  };
  clearFocusTimmer = () => {
    this.focusTimer && clearTimeout(this.focusTimer);
  };
  handleFocus = () => {
    this.clearBlurTimmer();
    this.focusTimer = setTimeout(() => {
      if (!this.isUnsafe) {
        this.setState({
          isMenuShown: true,
        });
      }
    }, 250);
    const { onFocus } = this.props;
    onFocus && onFocus();
  };

  handleSuggestionClick = event => {
    const { setArea } = this.props;
    if (!event.currentTarget.dataset.payload) return null;
    const { areaId, areaName, areaMaskingName, cityMaskingName } = JSON.parse(
      event.currentTarget.dataset.payload
    );

    let areaSelection = {
      areaId,
      areaName,
      areaMaskingName,
      cityMaskingName,
    };

    this.setState({
      input: areaName,
    });

    setArea(areaSelection);
  };

  render() {
    const { areaList, input, isFetching, isMenuShown } = this.state;
    const { cityId, cityName } = this.props.cityLocation;
    const selected = [
      {
        text: cityName,
        handleClick: this.props.removeCity,
        isBodyClickable: false,
      },
    ];
    const { inputRef, setClearButton, setFocus, placeholder } = this.props;

    return (
      <AutoComplete
        data-testing-id="area-selection"
        inputRef={inputRef}
        multiple={true}
        clearButton={setClearButton}
        label="Clear"
        isFocused={setFocus}
        isLoading={isFetching && !!input}
        isMenuShown={isMenuShown && areaList && areaList.length > 0}
        labelKey="result"
        maxHeight="300px"
        onChange={this.handleChange}
        onClear={this.handleClear}
        onClick={this.handleSuggestionClick}
        onFocus={this.handleFocus}
        onBlur={this.handleBlur}
        onKeyDown={this.handleKeyDown}
        placeholder={placeholder}
        results={areaList}
        selected={selected}
        text={input}
      />
    );
  }
}

AreaAutocomplete.propTypes = propTypes;
AreaAutocomplete.defaultProps = defaultProps;

export default AreaAutocomplete;
